import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context';
import { useSocketsContext } from '../../providers/SocketsProvider/Sockets.context';
import { useUserContext } from '../../providers/UserProvider/User.context';
import { ChallengeCenterService } from './ChallengeCenter.service';
import { setChallengeSignalsEnabled } from './ChallengeCenter.signals';
import {
  tChallengeCenterState,
  tChallengeFeedback,
  tChallengeKey,
  tDevTriggerAction,
} from './ChallengeCenter.types';

function completedMissionKeys(state: tChallengeCenterState): Set<tChallengeKey> {
  return new Set(
    (state.window?.challenges ?? [])
      .filter((c) => !!c.completedAt)
      .map((c) => c.key),
  );
}

// The day-8 answer to a week the server says is still running.
const HTTP_CONFLICT = 409;

export function useChallengeCenter() {
  const { currentWorkspaceId, workspaceInfo } = useWorkspaceContext();
  const { challengeUpdatedEvent } = useSocketsContext();
  const { currentUser } = useUserContext();
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<tChallengeCenterState | null>(null);
  // The workspace `state` was fetched for. A switch keeps the previous
  // workspace's state until the new fetch lands, and a slow answer for the
  // previous workspace can land after the switch.
  const [stateWorkspaceId, setStateWorkspaceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // The mission whose completed_at was first seen during THIS session, for the
  // "misión cumplida" toast. Null until the person dismisses it or nothing has
  // completed since the first load.
  const [celebratedKey, setCelebratedKey] = useState<tChallengeKey | null>(null);
  const loadedForRef = useRef<string | null>(null);
  // Completions already known for the current workspace. `null` means "no
  // state committed yet", which is what keeps a page reload from celebrating
  // missions finished last week: only a null -> set transition observed
  // between two commits in the same session counts.
  const knownCompletedRef = useRef<Set<tChallengeKey> | null>(null);

  // Every write of `state` goes through here so the completion transition is
  // detected in one place, whichever call produced the new state (initial
  // load, socket refetch, dev trigger, practiced).
  const commitState = useCallback(
    (next: tChallengeCenterState, workspaceId: string) => {
      // An answer for a workspace that is no longer selected would overwrite
      // the open workspace's state, so it is dropped.
      if (loadedForRef.current !== workspaceId) return;
      const completedNow = completedMissionKeys(next);
      const known = knownCompletedRef.current;
      if (known) {
        const fresh = [...completedNow].find((key) => !known.has(key));
        if (fresh) setCelebratedKey(fresh);
      }
      knownCompletedRef.current = completedNow;
      // UI-witnessed signals (stage editor opened, business verified) only
      // leave the browser while there is a week to mark them on.
      setChallengeSignalsEnabled(next.eligible && !!next.window);
      setState(next);
      setStateWorkspaceId(workspaceId);
    },
    [],
  );

  const refresh = useCallback(async () => {
    if (!currentWorkspaceId) return;
    setIsLoading(true);
    try {
      const next = await ChallengeCenterService.getChallenges(currentWorkspaceId);
      commitState(next, currentWorkspaceId);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspaceId, commitState]);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    if (loadedForRef.current === currentWorkspaceId) return;
    loadedForRef.current = currentWorkspaceId;
    knownCompletedRef.current = null;
    setChallengeSignalsEnabled(false);
    setCelebratedKey(null);
    void refresh();
  }, [currentWorkspaceId, refresh]);

  // Live update (SAD D7): workspace-service publishes workspace.challenge.updated
  // after every mission mutation; the websocket relay forwards it verbatim.
  // Refetch rather than trust the payload — same pattern the rest of this
  // provider uses for every other "something changed" event. useSubscription
  // is itself a hook (ahooks EventEmitter), so it must be called directly
  // here, never nested inside our own effect/callback.
  challengeUpdatedEvent.useSubscription(
    (payload: { workspace_id: string; user_auth_id: string }) => {
      if (payload.workspace_id !== currentWorkspaceId) return;
      if (currentUser?.authId && payload.user_auth_id !== currentUser.authId) return;
      void refresh();
    },
  );

  const trigger = useCallback(
    async (action: tDevTriggerAction) => {
      if (!currentWorkspaceId) return;
      const next = await ChallengeCenterService.devTrigger(currentWorkspaceId, action);
      commitState(next, currentWorkspaceId);
    },
    [currentWorkspaceId, commitState],
  );

  const markPracticed = useCallback(
    async (key: tChallengeKey) => {
      if (!currentWorkspaceId) return;
      const next = await ChallengeCenterService.practiced(currentWorkspaceId, key);
      commitState(next, currentWorkspaceId);
    },
    [currentWorkspaceId, commitState],
  );

  const giveFeedback = useCallback(
    async (feedback: tChallengeFeedback) => {
      if (!currentWorkspaceId) return;
      try {
        const next = await ChallengeCenterService.feedback(currentWorkspaceId, feedback);
        commitState(next, currentWorkspaceId);
      } catch (error) {
        // The server refuses an answer to a week that has not ended (409).
        // The panel only offers the question when it has, so a 409 means our
        // copy of the state is stale: refetch, and the question stays or goes
        // according to what comes back. Anything else (5xx, network) is a
        // real failure and stays observable, like every other call here.
        if (isAxiosError(error) && error.response?.status === HTTP_CONFLICT) {
          await refresh();
          return;
        }
        throw error;
      }
    },
    [currentWorkspaceId, commitState, refresh],
  );

  const dismissCelebration = useCallback(() => setCelebratedKey(null), []);

  const challenges = state?.window?.challenges ?? [];
  // An inactive workspace is parked on the plans page and can't act on any
  // mission, so the chip stays out of the way until it pays (QA O3). Only an
  // explicit `false` hides it: a workspace whose info is still loading keeps
  // the server's answer.
  const isWorkspaceInactive = workspaceInfo?.isActive === false;
  const completedCount = challenges.filter((c) => c.completedAt).length;

  return {
    isOpen,
    setIsOpen,
    isLoading,
    eligible: !isWorkspaceInactive && (state?.eligible ?? false),
    role: state?.role ?? null,
    // Null until this workspace's own state commits, so a caller can tell
    // "not loaded" from an assigned arm and never stamps another workspace's.
    holdout:
      stateWorkspaceId === currentWorkspaceId ? (state?.holdout ?? null) : null,
    hasWindow: !!state?.window,
    windowKind: state?.window?.kind ?? null,
    weekEnded: state?.window?.ended ?? false,
    feedback: state?.window?.feedback ?? null,
    giveFeedback,
    challenges,
    completedCount,
    total: challenges.length,
    gateKey: state?.window?.gateKey ?? null,
    trigger,
    markPracticed,
    celebratedKey,
    dismissCelebration,
  };
}
